import React, { useState } from "react";
import { getUsersByDate } from "../../api";
import toast, { Toaster } from 'react-hot-toast';
import { 
  UserGroupIcon,
  FunnelIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import * as XLSX from "xlsx";

const AllUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const handleFilter = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    try {
      const data = await getUsersByDate(startDate, endDate);
      setUsers(data);
      if (data.length === 0) {
        toast.info('No users found for the selected date range');
      } else {
        toast.success(`Found ${data.length} user${data.length !== 1 ? 's' : ''}`);
      }
    } catch (error) {
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setUsers([]);
    setHasSearched(false);
  };

  const exportToExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(users);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
      XLSX.writeFile(workbook, `exam_status_${startDate}_to_${endDate}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export to Excel');
    }
  };

  const completedCount = users.filter(u => u.estatus > 0).length;
  const pendingCount = users.filter(u => u.estatus === 0).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <UserGroupIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Exam Status by Date</h1>
                <p className="text-sm text-gray-500">Filter users by exam date range</p>
              </div>
            </div>

            {users.length > 0 && (
              <button
                onClick={exportToExcel}
                className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all shadow-md"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span>Export to Excel</span>
              </button>
            )}
          </div>

          {/* Filter Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
            <div className="flex items-center mb-4">
              <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h2 className="font-semibold text-gray-800">Date Range Filter</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
              </div>

              <div className="flex items-end gap-2">
                <button
                  onClick={handleFilter}
                  disabled={loading}
                  className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FunnelIcon className="h-5 w-5" />
                  <span>{loading ? 'Filtering...' : 'Filter'}</span>
                </button>

                {hasSearched && (
                  <button
                    onClick={handleReset}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all border border-gray-300"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          {users.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600 font-medium">Total Users</p>
                    <p className="text-3xl font-bold text-purple-800">{users.length}</p>
                  </div>
                  <UserGroupIcon className="h-10 w-10 text-purple-400" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600 font-medium">Completed</p>
                    <p className="text-3xl font-bold text-green-800">{completedCount}</p>
                  </div>
                  <CheckCircleIcon className="h-10 w-10 text-green-400" />
                </div>
              </div>

              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-600 font-medium">Pending</p>
                    <p className="text-3xl font-bold text-yellow-800">{pendingCount}</p>
                  </div>
                  <XCircleIcon className="h-10 w-10 text-yellow-400" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading users...</p>
          </div>
        )}

        {/* Table */}
        {!loading && hasSearched && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {users.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Name</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Class</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">School</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Parents No.</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Contact</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Exam Date</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users.map((user, index) => (
                        <tr key={user.uid} className={`hover:bg-gray-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <span className="text-sm font-semibold text-blue-600">
                                {user.uid}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-medium text-gray-800">{user.name}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              Class {user.class}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-600">{user.school}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{user.parents_number}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{user.contact_number}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{user.examdate}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                              user.estatus > 0 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {user.estatus > 0 ? (
                                <>
                                  <CheckCircleIcon className="h-4 w-4" />
                                  <span>Completed</span>
                                </>
                              ) : (
                                <>
                                  <XCircleIcon className="h-4 w-4" />
                                  <span>Pending</span>
                                </>
                              )}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>
                      Showing <span className="font-semibold text-gray-800">{users.length}</span> user{users.length !== 1 ? 's' : ''} from{' '}
                      <span className="font-semibold text-gray-800">{startDate}</span> to{' '}
                      <span className="font-semibold text-gray-800">{endDate}</span>
                    </span>
                    <div className="flex items-center space-x-4">
                      <span className="flex items-center space-x-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                        <span className="text-xs">Completed: {completedCount}</span>
                      </span>
                      <span className="flex items-center space-x-2">
                        <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                        <span className="text-xs">Pending: {pendingCount}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-12 text-center">
                <div className="flex flex-col items-center justify-center space-y-3">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <UserGroupIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">
                    No users found for the selected date range
                  </p>
                  <p className="text-sm text-gray-400">
                    Try selecting a different date range
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State */}
        {!loading && !hasSearched && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <CalendarIcon className="h-10 w-10 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Select Date Range</h3>
                <p className="text-gray-600">
                  Choose start and end dates to view exam status for users
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllUsers;
